'use client';

import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useMemo, useState } from 'react';

const ZODIAC_SIGNS = [
  { key: 'aries', label: 'Овен', dates: '21 мар – 20 апр' },
  { key: 'taurus', label: 'Телец', dates: '21 апр – 21 мая' },
  { key: 'gemini', label: 'Близнецы', dates: '22 мая – 21 июн' },
  { key: 'cancer', label: 'Рак', dates: '22 июн – 22 июл' },
  { key: 'leo', label: 'Лев', dates: '23 июл – 21 авг' },
  { key: 'virgo', label: 'Дева', dates: '22 авг – 23 сен' },
  { key: 'libra', label: 'Весы', dates: '24 сен – 23 окт' },
  { key: 'scorpio', label: 'Скорпион', dates: '24 окт – 22 ноя' },
  { key: 'sagittarius', label: 'Стрелец', dates: '23 ноя – 22 дек' },
  { key: 'capricorn', label: 'Козерог', dates: '23 дек – 20 янв' },
  { key: 'aquarius', label: 'Водолей', dates: '21 янв – 19 фев' },
  { key: 'pisces', label: 'Рыбы', dates: '20 фев – 20 мар' },
] as const;

type ZodiacSignKey = (typeof ZODIAC_SIGNS)[number]['key'];
type Mode = 'love' | 'work';

const getSignLabel = (key: ZodiacSignKey) => ZODIAC_SIGNS.find((s) => s.key === key)?.label ?? key;

export default function CompatibilityPickerClient({ mode }: { mode: Mode }) {
  const [sign1, setSign1] = useState<ZodiacSignKey | null>(null);

  const theme = mode === 'love' ? 'pink' : 'blue';

  const baseTitle1 = mode === 'love' ? 'Партнер 1 (Вы)' : 'Коллега 1 (Вы)';
  const baseTitle2 = mode === 'love' ? 'Партнер 2' : 'Коллега 2';

  const { title1, title2 } = useMemo(() => {
    const t1 = sign1 ? `${baseTitle1}: ${getSignLabel(sign1)}` : baseTitle1;
    const t2 = sign1 ? baseTitle2 : `${baseTitle2} (сначала выберите 1-го)`;
    return { title1: t1, title2: t2 };
  }, [sign1, baseTitle1, baseTitle2]);

  const handleSelect1 = useCallback((s: ZodiacSignKey) => {
    // повторный клик по выбранному — снять выбор (удобно)
    setSign1((prev) => (prev === s ? null : s));
  }, []);

  return (
    <div className="bg-gradient-to-b from-indigo-900 to-black rounded-2xl text-white p-4 sm:p-8">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-main">
          {mode === 'love' ? 'Любовная Совместимость' : 'Рабочая Совместимость'}
        </h1>
        <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
          Выберите два знака зодиака — результат откроется на отдельной странице.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row justify-center items-center gap-4 sm:gap-8">
        <ZodiacSelectorFirst
          title={title1}
          selectedSign={sign1}
          onSelectSign={handleSelect1}
          theme={theme}
        />

        <div
          className={`hidden lg:block text-5xl font-bold ${
            theme === 'pink' ? 'text-pink-400' : 'text-blue-400'
          }`}
        >
          +
        </div>

        <ZodiacSelectorSecond title={title2} sign1={sign1} mode={mode} theme={theme} />
      </div>
    </div>
  );
}

/* ------------------------- 1-й селектор: кнопки ------------------------- */

type ZodiacSelectorFirstProps = {
  title: string;
  selectedSign: ZodiacSignKey | null;
  onSelectSign: (sign: ZodiacSignKey) => void;
  theme: 'pink' | 'blue';
};

const ZodiacSelectorFirst = memo(function ZodiacSelectorFirst({
  title,
  selectedSign,
  onSelectSign,
  theme,
}: ZodiacSelectorFirstProps) {
  const handleClick = useCallback(
    (signKey: ZodiacSignKey) => onSelectSign(signKey),
    [onSelectSign],
  );

  const themeClasses = {
    title: theme === 'pink' ? 'text-pink-300' : 'text-blue-300',
    ring: theme === 'pink' ? 'focus-visible:ring-pink-500' : 'focus-visible:ring-blue-500',
    selected:
      theme === 'pink'
        ? 'bg-pink-600 border-pink-400 scale-110'
        : 'bg-blue-600 border-blue-400 scale-110',
  };

  return (
    <div className="w-full lg:w-auto">
      <h3
        className={`text-xl sm:text-2xl font-semibold text-center mb-6 min-h-[32px] ${themeClasses.title}`}
      >
        {title}
      </h3>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2 sm:gap-4">
        {ZODIAC_SIGNS.map((sign) => {
          const isSelected = selectedSign === sign.key;

          return (
            <button
              key={sign.key}
              type="button"
              onClick={() => handleClick(sign.key)}
              aria-pressed={isSelected}
              className={`
                group relative flex flex-col items-center p-2 sm:p-3 rounded-lg border-2 w-full
                transition-all duration-200
                focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${themeClasses.ring}
                ${isSelected ? themeClasses.selected : 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-gray-500'}
              `}
            >
              <Image
                src={`/horoscope/${sign.key}.svg`}
                alt=""
                aria-hidden="true"
                width={48}
                height={48}
                className="w-10 h-10 sm:w-12 sm:h-12"
              />
              <span className="mt-2 text-sm font-semibold">{sign.label}</span>

              <span
                className="
                  absolute -top-10 left-1/2 -translate-x-1/2
                  bg-black/80 text-white text-xs sm:text-sm px-3 py-1 rounded-md shadow-lg
                  opacity-0 group-hover:opacity-100 transition-opacity duration-300
                  pointer-events-none z-10 whitespace-nowrap
                "
              >
                {sign.dates}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

/* ------------------------- 2-й селектор: ссылки ------------------------- */

type ZodiacSelectorSecondProps = {
  title: string;
  sign1: ZodiacSignKey | null;
  mode: Mode;
  theme: 'pink' | 'blue';
};

const ZodiacSelectorSecond = memo(function ZodiacSelectorSecond({
  title,
  sign1,
  mode,
  theme,
}: ZodiacSelectorSecondProps) {
  const disabled = !sign1;

  const themeClasses = {
    title: theme === 'pink' ? 'text-pink-300' : 'text-blue-300',
    ring: theme === 'pink' ? 'focus-visible:ring-pink-500' : 'focus-visible:ring-blue-500',
    other:
      theme === 'pink'
        ? 'bg-blue-600 border-blue-400 opacity-70'
        : 'bg-pink-600 border-pink-400 opacity-70',
  };

  return (
    <div className={`w-full lg:w-auto ${disabled ? 'opacity-60' : ''}`}>
      <h3
        className={`text-xl sm:text-2xl font-semibold text-center mb-6 min-h-[32px] ${themeClasses.title}`}
      >
        {title}
      </h3>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2 sm:gap-4">
        {ZODIAC_SIGNS.map((sign) => {
          // если sign1 выбран — подсветим его во втором столбце как “занят”
          const isOtherSelected = sign1 === sign.key;

          const baseClass = `
            group relative flex flex-col items-center p-2 sm:p-3 rounded-lg border-2 w-full
            transition-all duration-200
            focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${themeClasses.ring}
            ${isOtherSelected ? themeClasses.other : 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-gray-500'}
          `;

          // Пока sign1 не выбран — это просто disabled кнопки (без навигации)
          if (disabled) {
            return (
              <button
                key={sign.key}
                type="button"
                disabled
                className={`${baseClass} cursor-not-allowed`}
              >
                <Image
                  src={`/horoscope/${sign.key}.svg`}
                  alt=""
                  aria-hidden="true"
                  width={48}
                  height={48}
                  className="w-10 h-10 sm:w-12 sm:h-12"
                />
                <span className="mt-2 text-sm font-semibold">{sign.label}</span>
              </button>
            );
          }

          // ✅ Навигация без router.push — это и убирает гонку/removeChild
          const href = `/horoscope/compatibility/${mode}/${sign1}/${sign.key}`;

          return (
            <Link
              key={sign.key}
              href={href}
              prefetch={false}
              aria-label={`Открыть результат: ${getSignLabel(sign1!)} + ${sign.label}`}
              className={baseClass}
            >
              <Image
                src={`/horoscope/${sign.key}.svg`}
                alt=""
                aria-hidden="true"
                width={48}
                height={48}
                className="w-10 h-10 sm:w-12 sm:h-12"
              />
              <span className="mt-2 text-sm font-semibold">{sign.label}</span>

              <span
                className="
                  absolute -top-10 left-1/2 -translate-x-1/2
                  bg-black/80 text-white text-xs sm:text-sm px-3 py-1 rounded-md shadow-lg
                  opacity-0 group-hover:opacity-100 transition-opacity duration-300
                  pointer-events-none z-10 whitespace-nowrap
                "
              >
                {sign.dates}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
});
